import type {
  AnalogPortConfig,
  DigitalPortConfig,
  SensorConfig,
  SensorType,
  TrackingObjectConfig,
} from './types'

// Pure, immutable edits over the whole config document. Each returns a NEW
// document with one section changed and the others preserved — so the PUT
// (which sends the whole document) never drops the untouched sections, and the
// react-query cache is never mutated in place (the mobile splice bug).
//
// Entries are keyed by their natural unique field (digital/analog port, sensor
// id per type), not array index — edits target a stable identity even if the
// list reorders. `update*` takes the original key so the key itself can change.

// --- Digital ports (key: port 0–3) ---

export function addDigitalPort(config: TrackingObjectConfig, item: DigitalPortConfig): TrackingObjectConfig {
  return { ...config, digitalPortConfigs: [...config.digitalPortConfigs, item] }
}

export function updateDigitalPort(
  config: TrackingObjectConfig,
  originalPort: number,
  item: DigitalPortConfig,
): TrackingObjectConfig {
  return {
    ...config,
    digitalPortConfigs: config.digitalPortConfigs.map((d) => (d.port === originalPort ? item : d)),
  }
}

export function removeDigitalPort(config: TrackingObjectConfig, port: number): TrackingObjectConfig {
  return { ...config, digitalPortConfigs: config.digitalPortConfigs.filter((d) => d.port !== port) }
}

// --- Analog ports (key: port string) ---

export function addAnalogPort(config: TrackingObjectConfig, item: AnalogPortConfig): TrackingObjectConfig {
  return { ...config, analogPortConfigs: [...config.analogPortConfigs, item] }
}

export function updateAnalogPort(
  config: TrackingObjectConfig,
  originalPort: string,
  item: AnalogPortConfig,
): TrackingObjectConfig {
  return {
    ...config,
    analogPortConfigs: config.analogPortConfigs.map((a) => (a.port === originalPort ? item : a)),
  }
}

export function removeAnalogPort(config: TrackingObjectConfig, port: string): TrackingObjectConfig {
  return { ...config, analogPortConfigs: config.analogPortConfigs.filter((a) => a.port !== port) }
}

// --- Sensors (key: type + id 0–3) ---

function setSensorList(
  config: TrackingObjectConfig,
  type: SensorType,
  list: SensorConfig[],
): TrackingObjectConfig {
  return { ...config, sensorConfiguration: { ...config.sensorConfiguration, [type]: list } }
}

export function addSensor(config: TrackingObjectConfig, type: SensorType, item: SensorConfig): TrackingObjectConfig {
  return setSensorList(config, type, [...config.sensorConfiguration[type], item])
}

export function updateSensor(
  config: TrackingObjectConfig,
  type: SensorType,
  originalId: number,
  item: SensorConfig,
): TrackingObjectConfig {
  return setSensorList(
    config,
    type,
    config.sensorConfiguration[type].map((s) => (s.id === originalId ? item : s)),
  )
}

export function removeSensor(config: TrackingObjectConfig, type: SensorType, id: number): TrackingObjectConfig {
  return setSensorList(
    config,
    type,
    config.sensorConfiguration[type].filter((s) => s.id !== id),
  )
}
